import { FEATURE_FLAGS } from '@mondaygpt/constants'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { ArrowLeft, Check, Facebook, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useIsMobile } from 'hooks'
import { useAnalytics } from 'hooks/use-analytics'
import { OpenAPI } from 'openapi'
import { MobilePageHeader } from '../../creative/components/MobilePageHeader'
import { useFeatureGate } from '../../feature-flags'
import { FacebookPagesApi, type FacebookPageSummary } from '../api/facebook-pages-api'
import { useFallbackConnect } from '../hooks/use-fallback-connect'
import { finishOAuthPickerStep } from '../utils/oauth-popup-complete'

export function FacebookPagePickerPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { track } = useAnalytics()
  const [params] = useSearchParams()
  const fromOAuth = params.get('fromOAuth') === '1'
  const businessId = params.get('businessId') ?? undefined

  const [pages, setPages] = useState<FacebookPageSummary[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const noPagesTracked = useRef(false)

  // Fallback path: when our own Meta app returns zero Pages (the Business-
  // Portfolio-owned cohort), the user can opt into connecting through a trusted
  // publishing partner whose Meta app already holds the permissions we lack.
  const fallbackEnabled = useFeatureGate(FEATURE_FLAGS.enableSocialFallback)
  const fallback = useFallbackConnect({ platform: 'facebook', fromOAuth })

  useEffect(() => {
    let cancelled = false
    FacebookPagesApi.list()
      .then(res => {
        if (cancelled) return
        const list = res.pages ?? []
        setPages(list)
        const usable = list.filter(page => page.canPublish)
        if (usable.length === 1) setSelectedId(usable[0].id)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load Facebook pages:', err)
        // A 400 here means we couldn't reach the user's Facebook account
        // (token gone/expired) — treat as "no pages" so reconnect is offered.
        setPages([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Size the zero-pages cohort once, mirroring the server-side signal.
  useEffect(() => {
    if (pages && pages.length === 0 && !noPagesTracked.current) {
      noPagesTracked.current = true
      track(AnalyticsEvents.Social.SOCIAL_OAUTH_CALLBACK_FAILED, {
        platform: 'facebook',
        error: 'No Facebook Pages available to select',
        step: 'no_facebook_pages',
      })
    }
  }, [pages, track])

  const handleConfirm = useCallback(async () => {
    if (!selectedId) return
    setSaving(true)
    setError(null)
    try {
      await FacebookPagesApi.select({ pageId: selectedId })
      finishOAuthPickerStep({ provider: 'facebook', fromOAuth, navigate })
    } catch (err) {
      console.error('Failed to save Facebook page:', err)
      setError('We could not save your selection. Please try again.')
      setSaving(false)
    }
  }, [fromOAuth, navigate, selectedId])

  const handleReconnect = useCallback(() => {
    setReconnecting(true)
    setError(null)
    const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : ''
    const rawBase = import.meta.env.VITE_API_URL
    const apiBaseUrl = typeof rawBase === 'string' ? rawBase : 'http://localhost:8000'
    let url = `${apiBaseUrl}/v1/auth/facebook?token=${encodeURIComponent(token)}&source=integrations_page&forceReauth=1`
    if (businessId) {
      url += `&businessId=${encodeURIComponent(businessId)}`
    }
    window.location.href = url
  }, [businessId])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      </div>
    )
  }

  const hasPages = (pages?.length ?? 0) > 0
  const hasUsablePages = pages?.some(page => page.canPublish) ?? false
  const emptyTitle = hasPages ? 'No Page you can post to' : 'No Facebook Page found'

  return (
    <div className={isMobile ? 'flex h-full flex-col' : 'mx-auto max-w-2xl px-6 py-6'}>
      {isMobile ? (
        <MobilePageHeader
          title={hasUsablePages ? 'Choose your Facebook Page' : emptyTitle}
          subtitle={
            hasUsablePages
              ? 'You connected Facebook. Pick the Page to publish to.'
              : 'Connect a Facebook Page you manage to publish.'
          }
        />
      ) : (
        <div className="mb-6 flex items-start gap-3">
          {!fromOAuth && (
            <button
              type="button"
              onClick={() => navigate('/integrations')}
              className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-[8px] border border-line-bright text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
              aria-label="Back to integrations"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div>
            <h1 className="text-xl font-semibold text-paper">
              {hasUsablePages ? 'Choose your Facebook Page' : emptyTitle}
            </h1>
            <p className="mt-1 text-sm text-paper-dim">
              {hasUsablePages
                ? 'Pick the Page Taka should publish to. You can change this anytime.'
                : 'Taka publishes to a Facebook Page, not a personal profile.'}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="alert mb-4" data-tone="error" role="alert">
          {error}
        </div>
      )}

      <div className={`space-y-3 ${isMobile ? 'px-4 py-4' : ''}`}>
        {hasPages &&
          pages!.map(page => {
            const isSelected = selectedId === page.id
            return (
              <button
                key={page.id}
                type="button"
                onClick={() => setSelectedId(page.id)}
                disabled={!page.canPublish}
                className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-shadow ${
                  !page.canPublish
                    ? 'cursor-not-allowed border-line bg-bg-2 opacity-50'
                    : isSelected
                      ? 'border-acid bg-[color:var(--acid-tint)] shadow-[0_0_0_3px_var(--acid-soft)] hover:shadow-sm'
                      : 'border-line bg-bg-2 hover:border-line-bright hover:shadow-sm'
                }`}
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-line bg-bg-3">
                  {page.picture ? (
                    <img src={page.picture} alt={page.name} className="h-10 w-10 object-cover" />
                  ) : (
                    <Facebook size={20} className="text-paper-faint" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-paper">{page.name}</span>
                  {!page.canPublish && (
                    <span className="t-body-sm block text-paper-faint">
                      Can’t post here. Ask an admin of{' '}
                      {page.businessName ?? 'its Business Portfolio'} for content access.
                    </span>
                  )}
                </div>

                <div className="flex-shrink-0">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                      isSelected
                        ? 'text-ink-on-acid border-acid bg-acid'
                        : 'border-line-bright bg-bg-3'
                    }`}
                  >
                    {isSelected && <Check size={14} />}
                  </div>
                </div>
              </button>
            )
          })}
        {hasUsablePages ? (
          <div className="flex justify-end gap-2 pt-2">
            {!fromOAuth && (
              <button
                type="button"
                onClick={() => navigate('/integrations')}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-4 py-2 text-[13px] text-paper transition-colors hover:bg-bg-3 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedId || saving}
              className="text-ink-on-acid inline-flex items-center gap-1.5 rounded-[8px] bg-acid px-4 py-2 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Saving…' : fromOAuth ? 'Continue' : 'Save'}
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-line bg-bg-2 p-5">
            {hasPages ? (
              <>
                <p className="text-[14px] font-medium text-paper">
                  You can see these Pages but can’t post to any of them.
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-paper-dim">
                  Ask an admin of the Business Portfolio that owns your Page to give you content
                  access in Meta Business Suite, then reconnect.
                </p>
              </>
            ) : (
              <>
                <p className="text-[14px] font-medium text-paper">
                  We couldn’t find a Facebook Page you manage.
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-paper-dim">
                  To publish to Facebook you need to be an <strong>admin of a Facebook Page</strong>{' '}
                  and grant Taka access to it. When you reconnect, make sure to keep your Page
                  checked and accept the Page permissions in the Facebook dialog.
                </p>
              </>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleReconnect}
                disabled={reconnecting}
                className="text-ink-on-acid inline-flex items-center gap-1.5 rounded-[8px] bg-acid px-4 py-2 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
              >
                {reconnecting && <Loader2 size={14} className="animate-spin" />}
                {reconnecting ? 'Reconnecting…' : 'Reconnect Facebook'}
              </button>
              {!fromOAuth && (
                <button
                  type="button"
                  onClick={() => navigate('/integrations')}
                  className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-4 py-2 text-[13px] text-paper transition-colors hover:bg-bg-3"
                >
                  Back to integrations
                </button>
              )}
            </div>

            {fallbackEnabled && !hasPages && (
              <div className="mt-5 border-t border-line pt-4">
                <p className="text-[13px] leading-relaxed text-paper-dim">
                  Is your Page owned by a <strong>Meta Business Portfolio</strong>? Connect through
                  Phyllo, our trusted publishing partner — Taka will then publish to your Page
                  automatically on your behalf.
                </p>
                {fallback.error && (
                  <p className="mt-2 text-[12px] text-[color:var(--tone-error,#e5484d)]">
                    {fallback.error}
                  </p>
                )}
                {fallback.pages === null ? (
                  <button
                    type="button"
                    onClick={() => void fallback.start()}
                    disabled={fallback.phase === 'connecting' || fallback.phase === 'listing'}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-4 py-2 text-[13px] font-semibold text-paper transition-colors hover:bg-bg-3 disabled:opacity-50"
                  >
                    {(fallback.phase === 'connecting' || fallback.phase === 'listing') && (
                      <Loader2 size={14} className="animate-spin" />
                    )}
                    {fallback.phase === 'connecting'
                      ? 'Opening Phyllo…'
                      : fallback.phase === 'listing'
                        ? 'Loading Pages…'
                        : 'Connect via Phyllo'}
                  </button>
                ) : fallback.pages.length === 0 ? (
                  <p className="mt-3 text-[13px] text-paper-dim">
                    No Pages were found through Phyllo for this account.
                  </p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {fallback.pages.map(page => (
                      <button
                        key={page.id}
                        type="button"
                        onClick={() => void fallback.selectPage(page.id)}
                        disabled={fallback.phase === 'selecting'}
                        className="flex w-full items-center gap-3 rounded-xl border border-line bg-bg-2 p-3 text-left transition-shadow hover:border-line-bright hover:shadow-sm disabled:opacity-50"
                      >
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-line bg-bg-3">
                          {page.picture ? (
                            <img
                              src={page.picture}
                              alt={page.name}
                              className="h-9 w-9 object-cover"
                            />
                          ) : (
                            <Facebook size={18} className="text-paper-faint" />
                          )}
                        </div>
                        <span className="min-w-0 flex-1 truncate font-medium text-paper">
                          {page.name}
                        </span>
                        {fallback.phase === 'selecting' && (
                          <Loader2 size={14} className="animate-spin text-paper-faint" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
